import PageHeader from "../components/PageHeader";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import EditorialMarquee from "../sections/EditorialMarquee";
import { PillButton } from "../components/PillButton";
import { Target, Heart, Zap, Users, ArrowUpRight } from "lucide-react";
import { STATS } from "../data/content";

const VALUES = [
  { icon: Heart, title: "Community first", desc: "Every decision starts with the residents and committees who live with our product daily." },
  { icon: Zap, title: "Radically simple", desc: "If your grandmother can't use it, we haven't finished designing it. Simplicity is a feature." },
  { icon: Target, title: "Transparent by default", desc: "Money, decisions, data — all visible to the people they belong to. No black boxes." },
  { icon: Users, title: "Built in India", desc: "Designed around how Indian societies actually operate, from RWAs to gated townships." },
];

const TIMELINE = [
  { year: "2021", text: "grooh starts as a weekend project to fix one founder's chaotic apartment WhatsApp group." },
  { year: "2022", text: "First 100 societies onboard. Maintenance billing and visitor management launch." },
  { year: "2024", text: "Cross 2,000 societies and 1M residents. Online payments process ₹100Cr+." },
  { year: "2025", text: "Launch community feed, polls and the all-new gate security suite." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        overline="About grooh"
        title="We turn buildings"
        highlight="into communities."
        subtitle="grooh is on a mission to make living in a shared community effortless, transparent and genuinely enjoyable — for every resident and every committee."
        testid="about-header"
      />

      {/* Image + intro */}
      <section className="pb-24">
        <div className="container-x grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-7">
            <div className="rounded-3xl overflow-hidden border-2 border-black shadow-hard">
              <img
                src="https://images.unsplash.com/photo-1625283518288-00362afc8663?auto=format&fit=crop&w=1400&q=80"
                alt="Community gathering"
                className="w-full h-[300px] md:h-[440px] object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight leading-tight">
              A calmer way to run the place you call home.
            </h2>
            <p className="mt-5 text-black/65 font-medium text-lg leading-relaxed">
              We started grooh after watching committee members burn out juggling registers,
              spreadsheets and endless group chats. There had to be a better way — so we built it.
            </p>
            <p className="mt-4 text-black/65 font-medium text-lg leading-relaxed">
              Today, thousands of societies trust grooh to handle the hard parts, so they can
              focus on the part that matters: community.
            </p>
          </Reveal>
        </div>
      </section>

      {/* <EditorialMarquee dark /> */}

      {/* Values */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight leading-[0.95] max-w-2xl mb-14">
              The values behind every build.
            </h2>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <StaggerItem key={v.title}>
                  <div className="h-full bg-brand-light rounded-3xl p-8 border-2 border-transparent hover:border-black transition-all duration-300" data-testid={`value-${i}`}>
                    <div className="w-14 h-14 rounded-2xl bg-[#0033A2] flex items-center justify-center mb-5">
                      <Icon size={26} className="text-white" strokeWidth={2} />
                    </div>
                    <h3 className="font-heading font-bold text-2xl tracking-tight mb-2">{v.title}</h3>
                    <p className="text-black/65 font-medium leading-relaxed">{v.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* Stats */}
      <section className="pb-24">
        <div className="container-x">
          <div className="bg-[#0033A2] text-white rounded-3xl border-2 border-black p-10 md:p-16">
            <p className="font-heading font-bold uppercase tracking-[0.2em] text-sm text-white/60 mb-10">
              grooh in numbers
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {STATS.map((s) => (
                <Reveal key={s.label}>
                  <p className="font-heading font-black text-4xl md:text-5xl tracking-tight">{s.value}</p>
                  <p className="mt-2 font-medium text-white/70 text-sm">{s.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="pb-24 md:pb-32">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight mb-14">
              Our journey so far.
            </h2>
          </Reveal>
          <div className="space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={0.03}>
                <div className="border-t-2 border-black py-8 md:flex gap-10 items-start" data-testid={`timeline-${i}`}>
                  <span className="font-heading font-black text-4xl md:text-5xl text-[#0033A2] tracking-tighter block mb-3 md:mb-0 md:w-40 shrink-0">
                    {t.year}
                  </span>
                  <p className="text-black/70 font-medium text-lg md:text-xl leading-relaxed max-w-2xl">
                    {t.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14">
              <PillButton to="/contact" testid="about-cta">
                Join the journey <ArrowUpRight size={18} strokeWidth={2.5} />
              </PillButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
